import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import axiosClient from 'utils/axiosClient'
import DialogConfirm from './Dialog'
import UploadImage from './UploadImage'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { useSnackbar } from 'notistack'

interface Props {
  open: boolean
  handleClose: () => void
  handleSelect: (img: string) => void
}

export default function ShowImages({ open, handleClose, handleSelect }: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const [images, setImages] = useState<string[]>([])
  const [selected, setSelected] = useState<string>('')
  const [showUpload, setShowUpload] = useState(false)
  const getData = async () => {
    const { data } = await axiosClient('/api/upload')
    setImages(data)
  }
  useEffect(() => {
    if (!showUpload) {
      getData()
    }
  }, [showUpload])

  const onSubmit = () => {
    handleSelect(selected)
  }
  const deleteFile = async () => {
    try {
      await axiosClient.delete('/api/upload/' + encodeURIComponent(selected))
      enqueueSnackbar('削除完了しました。', {
        variant: 'success',
      })
      setSelected('')
      getData()
    } catch (error) {
      console.log(error)
    }
  }
  return (
    <DialogConfirm
      disabled={!selected}
      open={open}
      handleClose={handleClose}
      handleSubmit={onSubmit}
      maxWidth="md"
      title={'画像一覧'}
      primaryButtonColor="secondary"
      primaryButtonText="選択する"
    >
      {showUpload && (
        <UploadImage
          open={showUpload}
          handleClose={() => setShowUpload(false)}
        />
      )}
      <Stack direction="row" gap={2} justifyContent="flex-end">
        {!!selected && (
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => deleteFile()}
          >
            <DeleteForeverIcon sx={{ mb: 0.2 }} />
            <Typography variant="button">物理削除</Typography>
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          color="primary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowUpload(true)}
        >
          <DriveFolderUploadIcon sx={{ mb: 0.2 }} />
          <Typography fontWeight="bold" variant="button">
            アップロード
          </Typography>
        </Button>
      </Stack>
      <Stack
        sx={{
          height: '100vmin',
          maxHeight: '70vh',
          overflow: 'auto',
          mt: 1,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} flexWrap="wrap">
          {images.map((img) => (
            <Box
              key={img}
              sx={{
                width: {
                  sm: '100%',
                  md: '25%',
                  lg: '20%',
                },
                p: 1,
                cursor: 'pointer',
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor:
                  selected === img
                    ? (theme) => theme.palette.secondary.light
                    : 'transparent',
                background:
                  selected === img
                    ? (theme) => theme.palette.secondary.light
                    : 'transparent',
                color:
                  selected === img
                    ? (theme) => theme.palette.secondary.contrastText
                    : 'inherit',
                boxShadow: '0 0 1px rgba(0,0,0,0.3)',
                '&:hover': {
                  borderColor: (theme) => theme.palette.secondary.light,
                },
              }}
              onClick={() => setSelected(img)}
              onDoubleClick={onSubmit}
            >
              <Box
                component="div"
                width="100%"
                pt="100%"
                sx={{
                  backgroundImage: `url(${img})`,
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'cover',
                }}
              ></Box>
              <Typography
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {img.split('/').pop()}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </DialogConfirm>
  )
}
